import * as yup from "yup";
import { Container } from "./styles";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { IEventSession } from "../../providers/eventSessions/eventsSessions.interface";
import { useEffect, useState } from "react";
import { useTenant } from "../../providers/tenant";
import { useEventSession } from "../../providers/eventSessions";
import { useUser } from "../../providers/users";
import path from "path";
import { useEvent } from "../../providers/events";
import { toast } from "react-toastify";

interface IProps {
  eventSession: IEventSession | null;
  setEventSession: React.Dispatch<React.SetStateAction<IEventSession | null>>;
  handleComplete: () => void;
  handleBack: () => void;
}

const EventSessionIntegrationForm = ({
  eventSession,
  setEventSession,
  handleComplete,
  handleBack,
}: IProps) => {
  const navigate = useNavigate();
  const { path } = useParams();
  const { t } = useTranslation();
  const { editEventSession, sendExampleWebhook } = useEventSession();
  const { allEvents } = useEvent();
  const { tenant, getTenant } = useTenant();
  const { apiLoading } = useUser();
  const [SMSIntegration, setSMSIntegration] = useState(
    (eventSession?.integrations && eventSession?.integrations.sms) || ""
  );
  const [emailIntegration, setEmailIntegration] = useState(
    (eventSession?.integrations && eventSession?.integrations.email) || ""
  );
  const [voiceIntegration, setVoiceIntegration] = useState(
    (eventSession?.integrations && eventSession?.integrations.audio) || ""
  );
  const [whatsappIntegration, setWhatsappIntegration] = useState(
    (eventSession?.integrations && eventSession?.integrations.whatsapp) || ""
  );
  const [openAiIntegration, setOpenAiIntegration] = useState(
    (eventSession?.integrations && eventSession?.integrations.ai) || ""
  );
  const [ipIntegration, setIpIntegration] = useState(
    (eventSession?.integrations && eventSession?.integrations.ipService) || ""
  );
  const [shortsIntegration, setShortsIntegration] = useState(
    (eventSession?.integrations && eventSession?.integrations.shorts) || ""
  );
  const [webhookWatchedLive, setWebhookWatchedLive] = useState(
    (eventSession?.integrations &&
      eventSession?.integrations.webhookWatchedLive) ||
      ""
  );
  const [webhookWatchedReplay, setWebhookWatchedReplay] = useState(
    (eventSession?.integrations &&
      eventSession?.integrations.webhookWatchedReplay) ||
      ""
  );
  const [webhookRegistered, setWebhookRegistered] = useState(
    (eventSession?.integrations &&
      eventSession?.integrations.webhookRegistered) ||
      ""
  );
  const [testingWebhook, setTestingWebhook] = useState({
    watchedLive: false,
    watchedReplay: false,
    registered: false,
  });

  const handleChangeShortsIntegration = (event: any) => {
    setShortsIntegration(event.target.value);
  };
  const handleChangeIpIntegration = (event: any) => {
    setIpIntegration(event.target.value);
  };
  const handleChangeOpenAiIntegration = (event: any) => {
    setOpenAiIntegration(event.target.value);
  };

  const handleChangeWhatsappIntegration = (event: any) => {
    setWhatsappIntegration(event.target.value);
  };

  const handleChangeVoiceIntegration = (event: any) => {
    setVoiceIntegration(event.target.value);
  };

  const handleChangeSMSIntegration = (event: any) => {
    setSMSIntegration(event.target.value);
  };

  const handleChangeEmailIntegration = (event: any) => {
    setEmailIntegration(event.target.value);
  };

  const handleChangeWebhookWatchedLive = (event: any) => {
    setWebhookWatchedLive(event.target.value);
  };

  const handleChangeWebhookWatchedReplay = (event: any) => {
    setWebhookWatchedReplay(event.target.value);
  };

  const handleChangeWebhookRegistered = (event: any) => {
    setWebhookRegistered(event.target.value);
  };

  const handleTestWebhook = async (type: string, webhookName: string) => {
    if (!webhookName) return;
    const eventName = allEvents.find(
      (e: any) => e.id === eventSession!.eventId
    ).name;
    try {
      // Set the correct testing state
      if (type === "WATCHED_LIVE") {
        setTestingWebhook((prev) => ({ ...prev, watchedLive: true }));
      } else if (type === "WATCHED_REPLAY") {
        setTestingWebhook((prev) => ({ ...prev, watchedReplay: true }));
      } else if (type === "SIGNED_UP") {
        setTestingWebhook((prev) => ({ ...prev, registered: true }));
      }

      // Find the webhook URL from tenant
      const webhookConfig = tenant?.integrations?.webhooks?.find(
        (hook: any) => hook.name === webhookName
      );

      if (!webhookConfig || !webhookConfig.link) {
        console.error("Webhook URL not found");
        return;
      }

      // Call the sendExampleWebhook function
      await sendExampleWebhook({
        eventType: type,
        url: webhookConfig.link,
        sessionName: eventSession?.name,
        sessionTitle: eventSession?.title,
        sessionDescription: eventSession?.description,
        eventName: eventName,
      });

      // Success notification could be added here
    } catch (error) {
      toast.error("Failed to send test webhook");
      console.error("Failed to send test webhook:", error);
      // Error notification could be added here
    } finally {
      toast.success("Webhook sent");
      // Reset the testing state
      if (type === "WATCHED_LIVE") {
        setTestingWebhook((prev) => ({ ...prev, watchedLive: false }));
      } else if (type === "WATCHED_REPLAY") {
        setTestingWebhook((prev) => ({ ...prev, watchedReplay: false }));
      } else if (type === "SIGNED_UP") {
        setTestingWebhook((prev) => ({ ...prev, registered: false }));
      }
    }
  };

  const onSubmit = async (data: any) => {
    try {
      const updatedEventSession = {
        integrations: {
          email: emailIntegration,
          whatsapp: whatsappIntegration,
          audio: voiceIntegration,
          sms: SMSIntegration,
          shorts: shortsIntegration,
          ai: openAiIntegration,
          webhookWatchedLive,
          webhookWatchedReplay,
          webhookRegistered,
          ipService: ipIntegration,
        },
        thirdPartyScripts: {
          header: data.headerScripts,
          body: data.bodyScripts,
        },
      };
      if (!eventSession) {
        navigate(`/${path}/events`);
        return;
      }
      const response = await editEventSession(
        eventSession.id,
        updatedEventSession
      );
      setEventSession(response);
      handleComplete();
    } catch (error) {
      console.error("Failed to update event session:", error);
    }
  };

  useEffect(() => {
    if (!tenant) {
      getTenant();
    }
  }, [tenant, getTenant]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({});

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="integrationsDiv">
          <h3>Notification integrations</h3>
          <div>
            <FormControl>
              <InputLabel id="email-integration-label">
                Email Integration
              </InputLabel>
              <Select
                labelId="email-integration-label"
                id="email-integration-select"
                sx={{
                  boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
                  "& fieldset": { border: "none" },
                  backgroundColor: "#FFF",
                  width: 442,
                }}
                value={emailIntegration}
                label="Email Integration"
                {...register("emailIntegration")}
                onChange={handleChangeEmailIntegration}
              >
                {tenant && tenant.integrations.postmark ? (
                  <MenuItem value={"postmark"}>Postmark</MenuItem>
                ) : null}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel id="whatsapp-integration-label">
                Whatsapp Integration
              </InputLabel>
              <Select
                labelId="whatsapp-integration-label"
                id="whatsapp-integration-select"
                sx={{
                  boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
                  "& fieldset": { border: "none" },
                  backgroundColor: "#FFF",
                  width: 442,
                }}
                value={whatsappIntegration}
                label="Whatsapp Integration"
                {...register("whatsappIntegration")}
                onChange={handleChangeWhatsappIntegration}
              >
                {tenant && tenant.integrations.twilioWhatsapp ? (
                  <MenuItem value={"twilioWhatsapp"}>Twilio</MenuItem>
                ) : null}
              </Select>
            </FormControl>
          </div>
          <div>
            <FormControl>
              <InputLabel id="sms-integration-label">
                SMS Integration
              </InputLabel>
              <Select
                labelId="sms-integration-label"
                id="sms-integration-select"
                sx={{
                  boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
                  "& fieldset": { border: "none" },
                  backgroundColor: "#FFF",
                  width: 442,
                }}
                value={SMSIntegration}
                label="SMS Integration"
                {...register("SMSIntegration")}
                onChange={handleChangeSMSIntegration}
              >
                {tenant && tenant.integrations.twilio ? (
                  <MenuItem value={"twilio"}>Twilio</MenuItem>
                ) : null}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel id="voice-integration-label">
                Voice Integration
              </InputLabel>
              <Select
                labelId="voice-integration-label"
                id="voice-integration-select"
                sx={{
                  boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
                  "& fieldset": { border: "none" },
                  backgroundColor: "#FFF",
                  width: 442,
                }}
                value={voiceIntegration}
                label="Voice Integration"
                {...register("voiceIntegration")}
                onChange={handleChangeVoiceIntegration}
              >
                {tenant && tenant.integrations.twilioAudio ? (
                  <MenuItem value={"twilioAudio"}>Twilio</MenuItem>
                ) : null}
              </Select>
            </FormControl>
          </div>
        </div>

        <div className="integrationsDiv">
          <h3>Other integrations</h3>
          <div>
            <FormControl>
              <InputLabel id="ai-integration-label">AI Integration</InputLabel>
              <Select
                labelId="ai-integration-label"
                id="ai-integration-select"
                sx={{
                  boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
                  "& fieldset": { border: "none" },
                  backgroundColor: "#FFF",
                  width: 442,
                }}
                value={openAiIntegration}
                label="AI Integration"
                {...register("openAiIntegration")}
                onChange={handleChangeOpenAiIntegration}
              >
                {tenant && tenant.integrations.openai ? (
                  <MenuItem value={"openai"}>OpenAI</MenuItem>
                ) : null}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel id="shorts-integration-label">
                Short Integration
              </InputLabel>
              <Select
                labelId="shorts-integration-label"
                id="shorts-integration-select"
                sx={{
                  boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
                  "& fieldset": { border: "none" },
                  backgroundColor: "#FFF",
                  width: 442,
                }}
                value={shortsIntegration}
                label="Short Integration"
                {...register("shortsIntegration")}
                onChange={handleChangeShortsIntegration}
              >
                {tenant && tenant.integrations.shorts ? (
                  <MenuItem value={"shorts"}>Short</MenuItem>
                ) : null}
              </Select>
            </FormControl>
          </div>
          <div>
            <FormControl>
              <InputLabel id="ip-integration-label">Ip Integration</InputLabel>
              <Select
                labelId="ip-integration-label"
                id="ip-integration-select"
                sx={{
                  boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
                  "& fieldset": { border: "none" },
                  backgroundColor: "#FFF",
                  width: 442,
                }}
                value={ipIntegration}
                label="ip Integration"
                {...register("ipIntegration")}
                onChange={handleChangeIpIntegration}
              >
                {tenant && tenant.integrations.ipgeolocation ? (
                  <MenuItem value={"ipgeolocation"}>Ip Geolocation</MenuItem>
                ) : null}
              </Select>
            </FormControl>
          </div>
        </div>

        <div className="integrationsDiv">
          <h3>Webhook integrations</h3>
          <div className="webhookIntegrations">
            <div className="webhookContainer">
              <FormControl>
                <InputLabel id="webhook-watched-live-label" shrink={true}>
                  Webhook Watched Live
                </InputLabel>
                <Select
                  labelId="webhook-watched-live-label"
                  id="webhook-watched-live-select"
                  sx={{
                    boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
                    "& fieldset": { border: "none" },
                    backgroundColor: "#FFF",
                    width: "100%",
                  }}
                  value={webhookWatchedLive}
                  label="Webhook Watched Live"
                  {...register("webhookWatchedLive")}
                  onChange={handleChangeWebhookWatchedLive}
                  displayEmpty
                  renderValue={(value) => (value === "" ? "None" : value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {tenant &&
                    tenant.integrations.webhooks &&
                    tenant.integrations.webhooks.map((webhook: any) => (
                      <MenuItem key={webhook.link} value={webhook.name}>
                        {webhook.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Tooltip
                title={
                  webhookWatchedLive
                    ? "Send a test webhook"
                    : "Select a webhook first"
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!webhookWatchedLive || testingWebhook.watchedLive}
                    onClick={() =>
                      handleTestWebhook("WATCHED_LIVE", webhookWatchedLive)
                    }
                    sx={{ mt: 1, borderRadius: "20px" }}
                  >
                    {testingWebhook.watchedLive ? "Sending..." : "Test Webhook"}
                  </Button>
                </span>
              </Tooltip>
            </div>

            <div className="webhookContainer">
              <FormControl>
                <InputLabel id="webhook-watched-replay-label" shrink={true}>
                  Webhook Watched Replay
                </InputLabel>
                <Select
                  labelId="webhook-watched-replay-label"
                  id="webhook-watched-replay-select"
                  sx={{
                    boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
                    "& fieldset": { border: "none" },
                    backgroundColor: "#FFF",
                    width: "100%",
                  }}
                  value={webhookWatchedReplay}
                  label="Webhook Watched Replay"
                  {...register("webhookWatchedReplay")}
                  onChange={handleChangeWebhookWatchedReplay}
                  displayEmpty
                  renderValue={(value) => (value === "" ? "None" : value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {tenant &&
                    tenant.integrations.webhooks &&
                    tenant.integrations.webhooks.map((webhook: any) => (
                      <MenuItem key={webhook.link} value={webhook.name}>
                        {webhook.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Tooltip
                title={
                  webhookWatchedReplay
                    ? "Send a test webhook"
                    : "Select a webhook first"
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={
                      !webhookWatchedReplay || testingWebhook.watchedReplay
                    }
                    onClick={() =>
                      handleTestWebhook("WATCHED_REPLAY", webhookWatchedReplay)
                    }
                    sx={{ mt: 1, borderRadius: "20px" }}
                  >
                    {testingWebhook.watchedReplay
                      ? "Sending..."
                      : "Test Webhook"}
                  </Button>
                </span>
              </Tooltip>
            </div>

            <div className="webhookContainer">
              <FormControl>
                <InputLabel id="webhook-registered-label" shrink={true}>
                  Webhook Registered
                </InputLabel>
                <Select
                  labelId="webhook-registered-label"
                  id="webhook-registered-select"
                  sx={{
                    boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
                    "& fieldset": { border: "none" },
                    backgroundColor: "#FFF",
                    width: "100%",
                  }}
                  value={webhookRegistered}
                  label="Webhook Registered"
                  {...register("webhookRegistered")}
                  onChange={handleChangeWebhookRegistered}
                  displayEmpty
                  renderValue={(value) => (value === "" ? "None" : value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {tenant &&
                    tenant.integrations.webhooks &&
                    tenant.integrations.webhooks.map((webhook: any) => (
                      <MenuItem key={webhook.link} value={webhook.name}>
                        {webhook.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Tooltip
                title={
                  webhookRegistered
                    ? "Send a test webhook"
                    : "Select a webhook first"
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!webhookRegistered || testingWebhook.registered}
                    onClick={() =>
                      handleTestWebhook("SIGNED_UP", webhookRegistered)
                    }
                    sx={{ mt: 1, borderRadius: "20px" }}
                  >
                    {testingWebhook.registered ? "Sending..." : "Test Webhook"}
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="integrationsDiv">
          <h3>Advanced settings</h3>
          <div className="advancedSettings">
            <FormControl fullWidth>
              <TextField
                id="header-scripts"
                label="Header Scripts"
                defaultValue={
                  eventSession &&
                  eventSession.thirdPartyScripts &&
                  eventSession.thirdPartyScripts.header
                }
                multiline
                minRows={4}
                {...register("headerScripts")}
                sx={{
                  backgroundColor: "#FFF",
                  boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="body-scripts"
                label="Body Scripts"
                multiline
                minRows={4}
                defaultValue={
                  eventSession &&
                  eventSession.thirdPartyScripts &&
                  eventSession.thirdPartyScripts.body
                }
                {...register("bodyScripts")}
                sx={{
                  backgroundColor: "#FFF",
                  boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
            </FormControl>
          </div>
        </div>

        <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
          <Button
            color="inherit"
            onClick={handleBack}
            disabled={apiLoading}
            sx={{ mr: 1, borderRadius: "25px" }}
          >
            Back
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          <Button
            type="submit"
            disabled={apiLoading}
            variant="contained"
            sx={{ mr: 1, borderRadius: "25px" }}
          >
            Save & Next
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default EventSessionIntegrationForm;
